import { makeObservable } from 'mobx';
import { Store } from '../../store/store';
import { StoreNode } from '../../store';
import { createBrowserHistory, type History, type Listener, type Location } from 'history';
import { RouteContext } from '../../routes/routeContext';
import { RouteDescriptor, RouteLookup, RouteVisit } from '../../routes/routeSchema';
import { getRouteLookup } from '../../routes/routeLookup';
import { matchPath } from 'react-router-dom';
import { Error } from '../../core/error';
import { INIT_DEBUGGER, TRACE, TRACE_SEPARATOR_DECENT } from '../../core/debug/debugMacros';
import last from 'lodash/last';

export class HistoryManager
  extends StoreNode {

  constructor(store: Store) {
    super(store);
    makeObservable(this);

    INIT_DEBUGGER(this, { color: 'black' });

    // initialize objects
    const history = createBrowserHistory();
    const { location } = history;

    this.history = history;
    this.lookup = getRouteLookup(this.store);

    // initialize page context
    const context = this.createContext(location);

    this.pageContext = context;
    this.contextHistory.push(context);

    this.registerVisit();

    // register listeners
    history.listen(
      this.historyListener);
  }

  readonly nodeType = 'HistoryManager';

  readonly lookup: RouteLookup;
  readonly history: History;
  readonly contextHistory: RouteContext[] = [];
  readonly visitHistory: RouteVisit[] = [];

  readonly pageContext: RouteContext;

  get context(): RouteContext {
    return last(this.contextHistory) ?? this.pageContext;
  }

  private createContext(location: Location) {
    return new RouteContext({
      location: location,
      descriptor: this.getDescriptor(location)
    });
  }

  private historyListener: Listener = ({ location, action }) => {

    const { contextHistory } = this;

    switch (action) {
      case 'PUSH': {
        const ctx = new RouteContext({
          location: location,
          descriptor: this.getDescriptor(location)
        });
        contextHistory.push(ctx);
      } break;

      case 'POP':
        contextHistory.pop();
        break;

      case 'REPLACE': {
        const ctx = new RouteContext({
          location: location,
          descriptor: this.getDescriptor(location)
        });
        contextHistory.pop();
        contextHistory.push(ctx);
      } break;
    }

    const { context } = this;
    this.registerVisit();

    TRACE_SEPARATOR_DECENT(this);
    TRACE(this, 'History event', action, location, context);
  }

  private registerVisit() {
    const { history } = this;
    const { context } = this;

    const pageVisit: RouteVisit = {
      action: history.action,
      context,
      index: this.visitHistory.length,
      historyIndex: window.history.length
    }

    this.visitHistory.push(pageVisit);
  }

  private getDescriptor(location: Location): RouteDescriptor {

    const { lookup } = this;
    const lookupPaths = Object.keys(lookup);

    for (const lookupPath of lookupPaths) {

      const desc = lookup[lookupPath];
      const pattern = {
        path: lookupPath,
        end: desc.exact !== false
      };

      if (location?.pathname && matchPath(pattern, location.pathname))
        return desc;
    }

    throw new Error('InternalError', `There should always be a descriptor for the wildcard (*) path.`);
  }

}
